/**
 * Client mirror of the escape and format checks in
 * backend/src/business/process/contract/script-asset-path.ts, so the upload row can say what is
 * wrong while the path is typed. The server stays the authority and is what actually refuses.
 *
 * The reserved names (process.yaml, tests/test-inputs.json, schemas/ and the rest) are
 * deliberately NOT mirrored. That list moves as projections are added, and a stale copy here would
 * block a path the server accepts. A reserved path passes this check and is refused on save, with
 * the server's own message in the save notification.
 */

/** Where the server stores a bare file name: the only directory Nextflow puts on PATH. */
const DEFAULT_ASSET_DIR = "bin/";

/** Returns why the server would refuse this path, or null when it passes these checks. */
export const assetPathProblem = (path: string): string | null => {
  // Not trimmed, same as the server: an invisible leading space is real on disk.
  if ("" === path) {
    return "A script asset needs a path.";
  }
  if (path.includes("\\")) {
    return `Script asset path must use forward slashes: "${path}"`;
  }
  if (path.startsWith("/") || /^[A-Za-z]:/.test(path)) {
    return `Script asset path must be relative to the process directory: "${path}"`;
  }
  if (path.includes("\0")) {
    return "Script asset path contains a null byte.";
  }
  const segments = path.split("/");
  if (segments.some(segment => ".." === segment)) {
    return `Script asset path must not contain a ".." segment: "${path}"`;
  }
  if (segments.some(segment => "" === segment)) {
    return `Script asset path must not contain an empty segment: "${path}"`;
  }
  if (segments.some(segment => "." === segment)) {
    return `Script asset path must not contain a "." segment: "${path}"`;
  }
  if (segments.some(segment => segment !== segment.trim())) {
    return `Script asset path segments must not have leading or trailing whitespace: "${path}"`;
  }
  return null;
};

/** The path the server will store: a bare name lands in bin/, anything else is kept as written. */
export const storedAssetPath = (path: string): string =>
  path.includes("/") ? path : `${DEFAULT_ASSET_DIR}${path}`;

/**
 * The execute bit a new upload starts with. bin/ is on PATH, so a file there is meant to be
 * called by name and needs it. A fixture under tests/ or a sourced helper elsewhere does not.
 */
export const defaultExecutable = (path: string): boolean =>
  storedAssetPath(path).startsWith(DEFAULT_ASSET_DIR);
